import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGuardarNomina, useNomina } from '@/hooks/useManoObra';
import { useSemanas } from '@/hooks/useReportes';
import { formatearCRC } from '@adelante/shared';
import { useState } from 'react';
import { toast } from 'sonner';

export function NominaSemanalPantalla() {
  const { data } = useNomina();
  const semanasQ = useSemanas();
  const guardar = useGuardarNomina();
  const filas = data?.data ?? [];
  const semanas = semanasQ.data?.data ?? [];

  const [abierto, setAbierto] = useState(false);
  const [semana, setSemana] = useState('');
  const [monto, setMonto] = useState('');
  const [teorico, setTeorico] = useState('122000');
  const [notas, setNotas] = useState('');

  const totalReg = filas.reduce((a, f) => a + Number(f.monto_nomina_directa), 0);

  function abrir(f?: (typeof filas)[number]) {
    if (f) {
      setSemana(String(f.semana_operativa_id));
      setMonto(String(f.monto_nomina_directa));
      setTeorico(String(f.costo_teorico_m2));
      setNotas(f.notas ?? '');
    } else {
      setSemana('');
      setMonto('');
      setTeorico('122000');
      setNotas('');
    }
    setAbierto(true);
  }

  async function handleGuardar() {
    if (!semana || !monto) return;
    try {
      await guardar.mutateAsync({
        semana_operativa_id: Number(semana),
        monto_nomina_directa: Number(monto),
        costo_teorico_m2: Number(teorico) || 122000,
        notas: notas || null,
      });
      toast.success('Nómina registrada');
      setAbierto(false);
    } catch (e) {
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Nómina Semanal</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Monto de nómina directa de M.O. por semana + costo teórico/m².
          </p>
        </div>
        <Button onClick={() => abrir()}>+ Registrar nómina</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Semanas con nómina" value={String(filas.length)} />
        <Kpi label="Total nómina registrada" value={formatearCRC(totalReg)} />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Semana</TableHead>
              <TableHead>Período</TableHead>
              <TableHead className="text-right">Nómina directa</TableHead>
              <TableHead className="text-right">Horas totales</TableHead>
              <TableHead className="text-right">Costo/hora</TableHead>
              <TableHead className="text-right">Costo teórico/m²</TableHead>
              <TableHead>Notas</TableHead>
              <TableHead className="text-right">Acción</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.semana_operativa_id}>
                <TableCell className="font-semibold">
                  Sem {f.numero_semana}/{f.anio}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {f.fecha_inicio} → {f.fecha_fin}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatearCRC(Number(f.monto_nomina_directa))}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {Number(f.horas_totales) > 0
                    ? Number(f.horas_totales).toLocaleString('es-CR', {
                        maximumFractionDigits: 2,
                      })
                    : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {Number(f.horas_totales) > 0
                    ? formatearCRC(Number(f.monto_nomina_directa) / Number(f.horas_totales))
                    : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                  {formatearCRC(Number(f.costo_teorico_m2))}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{f.notas ?? '—'}</TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => abrir(f)}>
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filas.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  Sin nóminas registradas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={abierto} onOpenChange={(o) => !o && setAbierto(false)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Registrar nómina semanal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1.5 block text-xs">Semana operativa</Label>
              <Select value={semana} onValueChange={setSemana}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  {semanas.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.anio} · Semana {s.numero_semana} ({s.estado})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Monto nómina directa (₡)</Label>
              <Input
                type="number"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="Ej: 25000000"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Costo teórico /m² (₡)</Label>
              <Input type="number" value={teorico} onChange={(e) => setTeorico(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">Notas</Label>
              <Input
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Opcional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGuardar} disabled={!semana || !monto || guardar.isPending}>
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </Card>
  );
}
